import { useFeedbacksControllerUnRegisterMutation } from '@app/api/tagged/feedback-api';
import { Button, CardSliderLayout, Img, Markdown, PageNavbar, SocialNetworks, Tag } from '@app/components';
import { SiteMap } from '@app/constants';
import { BrandCard } from '@app/features/brands';
import { FeedbackBanner } from '@app/features/feedback';
import { useEnhancedTranslation, useIdParam, useMedia } from '@app/hooks';
import { tMap } from '@app/i18n';
import { CardLinkProps, isEventCard } from '@app/models';
import NotFoundPage from '@app/pages/not-found';
import { Interval, format } from 'date-fns';
import { FC, useMemo, useState } from 'react';
import { useMyEventsQuery } from '../account';
import { getPhone } from '../auth';
import { EventCard } from './event-card';
import { useEventQuery } from './event-query.hook';
import { EventRegistrationForm } from './registration-form';
import { useRegistrationNeeded } from './registration-needed.hook';

import './event.page.scss';

const formatPeriod = (period: Interval): string => {
  return `${format(period.start, 'd MMMM')} — ${format(period.end, 'd MMMM')}`;
};

const EventPage: FC = () => {
  const { t, tRoot } = useEnhancedTranslation({ keyPrefix: 'events' });
  const { isMobile } = useMedia();

  const [regFormVisible, setRegFormVisible] = useState(false);

  const id = useIdParam();

  const { data: event, isError, isSuccess } = useEventQuery(id);
  const { data: myEvents } = useMyEventsQuery();
  const [unregister] = useFeedbacksControllerUnRegisterMutation();

  const relatedCards = useMemo(
    () => [...(event?.events ?? []), ...(event?.brands.map(({ logo, ...brand }) => brand) ?? [])],
    [event],
  );

  const isRegistrationNeeded = useRegistrationNeeded(event);
  const isRegistered = isRegistrationNeeded ? !!myEvents.find((e) => e.id === event?.id) : false;

  if (isError || (isSuccess && !event)) {
    return <NotFoundPage />;
  }

  return (
    <main className="event-page">
      <PageNavbar
        className="event-page__nav"
        title={tMap(event?.tags[0]?.name)}
        backText={t('goTo')}
        linkForward={`${SiteMap.MAP}/${event?.floor}/${event?.cornerId || ''}`}
        forwardText={event?.floor !== undefined ? tRoot('floors.floorNum', { floor: event.floor }) : undefined}
      />

      {!!event && (
        <section className="event-page__primary">
          <div className="event-page__left-block">
            <div className="event-page__title-content">
              <h1 className="event-page__title">{tMap(event.name)}</h1>
              <p className="event-page__details">
                <span className="event-page__period">{formatPeriod(event.period)}</span>
                {event.timeShow && event.startTime && event.endTime ? (
                  <span className="event-page__period">
                    {event.startTime} — {event.endTime}
                  </span>
                ) : null}
                {!isRegistrationNeeded && !isMobile && !!event.registrationType && (
                  <span className="event-page__free-entrance">{t(`registrationTypeV2.${event.registrationType}`)}</span>
                )}
              </p>
              <div className="event-page__tags">
                {event.tags.map((tag) => (
                  <Tag key={tag.id} {...tag} />
                ))}
              </div>
              <SocialNetworks className="event-page__social-links" model={event.socialNetworks} />
              {!isRegistrationNeeded && isMobile && !!event.registrationType && (
                <p className="event-page__free-entrance">{t(`registrationTypeV2.${event.registrationType}`)}</p>
              )}
            </div>
            {isRegistrationNeeded && (
              <div className="event-page__reg-needed">
                <p className="event-page__reg-needed-label">{t(`registrationTypeV2.${event.registrationType}`)}</p>
                {isRegistered ? (
                  <Button
                    className="event-page__reg-btn"
                    onClick={() => unregister({ shortId: event.id, phone: getPhone() })}
                  >
                    {t('unregister')}
                  </Button>
                ) : (
                  <Button className="event-page__reg-btn" onClick={() => setRegFormVisible(true)}>
                    {t('register')}
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="event-page__right-block">
            <Markdown value={event.descriptions[0]} className="event-page__description" />
            <Markdown
              value={event.descriptions[1]}
              className="event-page__description event-page__description--faint"
            />
          </div>
          <picture className="event-page__picture">
            <Img src={event.banner?.url} alt={tMap(event.name)} className="event-page__image" />
            <span className="event-page__picture-text" style={{ color: event.fontColor }}>
              {tMap(event.name)}
            </span>
          </picture>
        </section>
      )}

      <FeedbackBanner className="event-page__feedback-banner" entityId={event?.id} />

      {/* TODO move to separate component */}
      {!!event?.events.length && (
        <section className="event-page__related">
          <h2 className="event-page__subtitle">
            {tRoot('common.related', {
              entities: [
                !!event.events.length && tRoot('events.title').toLowerCase(),
                !!event.brands.length && tRoot('brands.title').toLowerCase(),
              ].filter(Boolean),
            })}
          </h2>
          <CardSliderLayout
            cardClassName="event-page__related-card"
            cards={relatedCards}
            CardFn={(card) => (isEventCard(card) ? (EventCard as FC<CardLinkProps>) : (BrandCard as FC<CardLinkProps>))}
          />
        </section>
      )}

      {!!event && (
        <EventRegistrationForm open={regFormVisible} event={event} onClose={() => setRegFormVisible(false)} />
      )}
    </main>
  );
};

export default EventPage;
